import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { User } from '@shared/models/user';
import { LoadingState } from '@shared/types/loading-state';
import { GroupUserFilters } from '@shared/models/user-filters';
import { loadGroupUsers, loadGroupUsersFailure, loadGroupUsersSuccess } from '../actions/load-group-users.actions';

export const featureKey = 'group-users';

export const usersAdapter = createEntityAdapter<User>({
  selectId: (user: User) => user.id,
});

export interface State extends EntityState<User> {
  loadingState: LoadingState;
}

export const initialState: State = usersAdapter.getInitialState({
  loadingState: 'not_loading',
});

export const usersReducer = createReducer(
  initialState,
  on(loadGroupUsers, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
  })),
  on(loadGroupUsersSuccess, (state, { data }): State => {
    return usersAdapter.setAll(data, {
      ...state,
      loadingState: 'loaded' as LoadingState,
    });
  }),

  on(loadGroupUsersFailure, (state) => ({
    ...state,
    loadingState: 'not_loading' as LoadingState,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return usersReducer(state, action);
}

export const groupUsersFeatureSelector = createFeatureSelector<State>(featureKey);

const { selectIds, selectEntities, selectAll, selectTotal } = usersAdapter.getSelectors(groupUsersFeatureSelector);

export const selectAllGroupUsers = selectAll;

export const selectGroupUsersLoadingState = createSelector(
  groupUsersFeatureSelector,
  (state: State) => state.loadingState,
);

export const selectFilteredGroupUsers = (filters: GroupUserFilters) =>
  createSelector(selectAllGroupUsers, (allUsers) => {
    let users = allUsers;
    let noFilterValues = true;

    (Object.keys(filters) as Array<keyof GroupUserFilters>).forEach((key) => {
      const value = filters[key];
      let stringValue = value !== undefined ? value.toString() : '';
      if (stringValue.trim() !== '') {
        noFilterValues = false;
        const filteredUsers: User[] = [];
        users.forEach((user) => {
          if (typeof user.attributes[key] === 'boolean') {
            if ((user.attributes[key] as string) === stringValue) {
              filteredUsers.push(user);
            }
          } else if ((user.attributes[key] as string)?.toLowerCase().includes(stringValue.toLowerCase())) {
            filteredUsers.push(user);
          }
        });
        users = filteredUsers;
      }
    });

    return noFilterValues ? allUsers : users;
  });

export const selectGroupUserById = (userId: string) =>
  createSelector(selectEntities, (entities) => {
    return entities[userId];
  });
